import React from 'react';
import { usePullToRefresh } from '../../utils/usePullToRefresh';

export const PullToRefresh = ({
  onRefresh,
  children,
  pullingContent,
  refreshContent,
  pullDownThreshold = 60,
  maxPullDownDistance = 120,
  resistance = 2.5,
  refreshThreshold = 70,
  className = '',
  disabled = false,
  ...props
}) => {
  const {
    containerRef,
    pullDistance,
    isRefreshing,
    isPulling,
  } = usePullToRefresh(onRefresh, {
    pullDownThreshold,
    maxPullDownDistance,
    resistance,
    refreshThreshold,
  });

  if (disabled) {
    return (
      <div className={className} {...props}>
        {children}
      </div>
    );
  }

  // Calculate progress for circular indicator (0-100)
  const pullProgress = Math.min((pullDistance / refreshThreshold) * 100, 100);

  const showPullingContent = isPulling && pullDistance > 0 && !isRefreshing;
  const showRefreshContent = isRefreshing;

  // Circular Progress Component
  const CircularProgress = ({ progress, size = 30, strokeWidth = 3 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDasharray = `${circumference} ${circumference}`;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="flex items-center justify-center">
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="rgba(248, 243, 243, 1)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#FF7F00"
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-200 ease-out"
          />
        </svg>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`pull-to-refresh-container relative touch-pan-y ${className}`}
      style={{
        transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
        transition: isPulling ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
      {...props}
    >
      {/* Pull indicator */}
      {(showPullingContent || showRefreshContent) && (
        <div
          className="pull-indicator flex items-center justify-center p-3 bg-white to-amber-500 text-white rounded-full shadow-2xl border-4 border-white transition-all duration-300"
          style={{
            position: 'absolute',
            top: '-10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            width: '30px',
            height: '30px',
          }}
        >
          {showRefreshContent ? (
            refreshContent || (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                <span>Refreshing...</span>
              </div>
            )
          ) : showPullingContent ? (
            <CircularProgress progress={pullProgress} />
          ) : null}
        </div>
      )}

      {/* Content */}
      <div
        className="pull-to-refresh-content will-change-transform"
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: isPulling ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {children}
      </div>
    </div>
  );
};
